import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@reservamos/elements';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import CSSVariablesProvider from './CSSVariablesProvider';

/**
 * Modal component with theme.
 *
 * @param {Object} props - The component props.
 * @param {string} props.title - The title of the modal.
 * @param {React.ReactNode} props.footer - The footer content of the modal.
 * @param {boolean} props.removeContentPadding - Whether to remove content padding in the modal.
 * @param {boolean} props.hasBlushing - Whether the modal has a blushing effect.
 * @param {boolean} props.expandedModal - Whether the modal is expanded.
 * @param {function} props.onCloseModal - The function to call when the modal is closed.
 * @param {React.ReactNode} props.children - The content of the modal.
 * @param {React.ReactNode} props.responsiveSize - Select size of mobile modal
 * @param {boolean} props.borderRadiusBigger - Whether the modal has a bigger border radius.
 * @param {boolean} props.closeOnOverlayClick - Whether the modal should close when the overlay is clicked.
 * @param {('XS'|'S'|'M'|'L')} props.rounded - The rounded border size of the modal.
 * @param {boolean} props.defaultOpened - Whether the modal is opened by default.
 * @param {boolean} props.centeredOnMobile - Whether the modal is centered on mobile devices.
 * @param {(''|'auto'|'S'|'M'|'L'|'XL')} props.contentHeight - The height of the modal content.
 * @param {React.ReactNode} props.modalTrigger - Element that opens the modal when clicked (trigger pattern).
 * @returns {React.ReactNode} The rendered modal component.
 */
const ModalWithTheme = ({
  title,
  footer,
  removeContentPadding,
  hasBlushing,
  expandedModal,
  onCloseModal,
  children,
  responsiveSize,
  borderRadiusBigger,
  closeOnOverlayClick,
  rounded,
  defaultOpened,
  centeredOnMobile,
  contentHeight,
  modalTrigger,
}) => {
  const { theme, brand } = useWhitelabelEnvs();
  const { FUNNEL_STYLE } = useWhitelabelFeatures();

  return (
    <Modal
      footer={footer}
      removeContentPadding={removeContentPadding}
      title={title}
      hasBlushing={hasBlushing}
      expandedModal={expandedModal}
      onCloseModal={onCloseModal}
      responsiveSize={responsiveSize}
      borderRadiusBigger={borderRadiusBigger}
      closeOnOverlayClick={closeOnOverlayClick}
      rounded={rounded}
      defaultOpened={defaultOpened}
      centeredOnMobile={centeredOnMobile}
      contentHeight={contentHeight}
      modalTrigger={modalTrigger}
    >
      <CSSVariablesProvider
        theme={theme}
        funnelStyle={FUNNEL_STYLE}
        funnelStyleBrandVariation={brand.toUpperCase()}
      >
        {children}
      </CSSVariablesProvider>
    </Modal>
  );
};

ModalWithTheme.propTypes = {
  title: PropTypes.string,
  footer: PropTypes.node,
  removeContentPadding: PropTypes.bool,
  hasBlushing: PropTypes.bool,
  expandedModal: PropTypes.bool,
  onCloseModal: PropTypes.func,
  children: PropTypes.node,
  responsiveSize: PropTypes.string,
  borderRadiusBigger: PropTypes.bool,
  closeOnOverlayClick: PropTypes.bool,
  rounded: PropTypes.oneOf(['XS', 'S', 'M', 'L']),
  contentHeight: PropTypes.oneOf(['', 'auto', 'S', 'M', 'L', 'XL']),
  defaultOpened: PropTypes.bool,
  centeredOnMobile: PropTypes.bool,
  modalTrigger: PropTypes.node,
};

ModalWithTheme.defaultProps = {
  contentHeight: 'auto',
};

export default ModalWithTheme;
